// جدولة تنبيهات الأذان.
//
// النسخة الأولى كانت تعرض زر تفعيل (غير مربوط أصلاً بالواجهة) يُظهر إشعاراً
// واحداً فورياً ويقول "سيتم تنبيهك عند دخول وقت الأذان" — بلا أي جدولة.
//
// ثم تبيّن على محاكي أندرويد أن الاعتماد على Notification API وحده يجعل
// الميزة معطّلة على المنصّة التي تدعمها أفضل من غيرها: WebView لا يوفّر
// window.Notification، فكان التطبيق يقول "متصفحك لا يدعم الإشعارات".
//
// الآن مساران:
//  • داخل تطبيق Capacitor → LocalNotifications، تُجدول على مستوى النظام
//    وتعمل والتطبيق مغلق.
//  • في المتصفح → Notification API، تعمل ما دام التطبيق مفتوحاً.
// الواجهة تذكر الفرق صراحة بدل الإيحاء بما لا يحدث.

import { getPrayerTimes, prayerLabel } from './prayerTimes';

const PRAYER_KEYS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

// setTimeout يفيض فوق ~24.8 يوم؛ نجدول ليومين فقط لكن نحرس على كل حال
const MAX_TIMEOUT = 2_147_483_647;
const NATIVE_ID_BASE = 9000;
const MAX_NATIVE = 24;

/**
 * قناة الأذان، منفصلة عن قناة الإعلانات.
 *
 * الفصل ليس ترتيباً: من أراد الأذان ولا يريد إعلانات المجالس يكتم
 * تلك وحدها من إعدادات النظام دون أن يفقد هذه. وأندرويد ٨ فأحدث
 * يُسقط أي إشعار لقناة غير موجودة بصمت، فتُنشأ قبل أول جدولة.
 */
const ADHAN_CHANNEL = {
  id: 'moslam_adhan',
  name: 'تنبيه الأذان',
  description: 'تنبيه عند دخول وقت كل صلاة حسب موقعك',
  importance: 5, // الأذان لا يؤجَّل: يظهر فوق الشاشة ويصدح
  visibility: 1,
  vibration: true,
};

let adhanChannelReady = false;

async function ensureAdhanChannel(plugin) {
  if (adhanChannelReady) return;
  try {
    await plugin.createChannel(ADHAN_CHANNEL);
  } catch {
    // iOS لا قنوات فيه، وإعادة الإنشاء بلا ضرر
  }
  adhanChannelReady = true;
}

let timers = [];

/* ----------------------------- كشف المنصّة ----------------------------- */

export const isNativeShell = () =>
  typeof window !== 'undefined' && Boolean(window.Capacitor?.isNativePlatform?.());

const nativePlugin = () => window.Capacitor?.Plugins?.LocalNotifications ?? null;

/** مُلحق الأذان — يرفع النداء صوتاً والتطبيق مغلق. */
const adhanPlugin = () => window.Capacitor?.Plugins?.Adhan ?? null;

const hasWebNotifications = () =>
  typeof window !== 'undefined' && 'Notification' in window;

/** هل يمكن للتنبيهات أن تعمل على هذه المنصّة أصلاً؟ */
export const isNotificationSupported = () =>
  isNativeShell() ? Boolean(nativePlugin()) : hasWebNotifications();

export function notificationPermission() {
  if (isNativeShell()) return 'prompt';
  return hasWebNotifications() ? Notification.permission : 'unsupported';
}

export async function requestNotificationPermission() {
  if (isNativeShell()) {
    const plugin = nativePlugin();
    if (!plugin) return 'unsupported';
    try {
      const res = await plugin.requestPermissions();
      return res?.display === 'granted' ? 'granted' : 'denied';
    } catch {
      return 'denied';
    }
  }

  if (!hasWebNotifications()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

/* ------------------------------- الجدولة ------------------------------- */

export function clearScheduledNotifications() {
  timers.forEach(clearTimeout);
  timers = [];

  const plugin = nativePlugin();
  if (plugin) {
    plugin
      .cancel({
        notifications: Array.from({ length: MAX_NATIVE }, (_, i) => ({ id: NATIVE_ID_BASE + i })),
      })
      .catch(() => {});
  }
}

function notifyWeb(title, body) {
  try {
    const n = new Notification(title, {
      body,
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      tag: 'moslam-adhan',
    });
    n.onclick = () => {
      window.focus();
      n.close();
    };
  } catch {
    // بعض المتصفحات تمنع الإنشاء المباشر خارج Service Worker
  }
}

/** يجمع أوقات الصلوات القادمة خلال اليومين التاليين. */
function upcomingPrayers({ lat, lng, methodId, madhab, timeZone }) {
  const now = new Date();
  const out = [];

  for (let offset = 0; offset <= 1; offset++) {
    const date = new Date(now);
    date.setDate(date.getDate() + offset);

    const { times } = getPrayerTimes({ lat, lng, date, methodId, madhab, timeZone });
    for (const key of PRAYER_KEYS) {
      const at = times[key];
      if (at instanceof Date && at.getTime() > now.getTime()) out.push({ key, at });
    }
  }

  return out.sort((a, b) => a.at - b.at);
}

/**
 * يجدول تنبيهات الصلوات القادمة.
 * @returns {number} عدد التنبيهات المجدولة
 */
export function schedulePrayerNotifications(options) {
  clearScheduledNotifications();

  const scheduled = upcomingPrayers(options);
  if (scheduled.length === 0) return 0;

  const { cityName = '' } = options;
  const body = cityName ? `حسب توقيت ${cityName}` : 'حان وقت الصلاة';

  if (isNativeShell()) {
    // جدولة على مستوى النظام: تعمل والتطبيق مغلق
    const plugin = nativePlugin();
    if (!plugin) return 0;

    // الأذان صوتاً: منبّه نظام مستقلّ عن الإشعار.
    //
    // الإشعار يُعلم، والأذان يُنادى. وصوت الإشعار يتبع مستوى الإشعارات
    // ويسكت مع عدم الإزعاج، فلا يصلح نداءً موقوتاً.
    scheduleAdhanCalls(scheduled, options);

    /*
      الدقّة بقدر الإذن، لا أكثر.

      إشعارٌ دقيق والإذن غائب يجعل الملحق يفتح صفحة «المنبّهات والتذكيرات»
      بنفسه — وهو غائبٌ افتراضاً على أندرويد ١٢ فأحدث، فكانت الصفحة تنفتح
      فوق التطبيق في كل إقلاع. والأذان صوتاً دقيقٌ في كل حال بمنبّهه
      (scheduleAdhanCalls)؛ الإشعار نصٌّ قد يتأخر دقائق بلا إذن. ومن أراد
      الدقة فزرّها في صفحة الأذان.
    */
    const exactAllowed = () =>
      plugin
        .checkExactNotificationSetting?.()
        .then((r) => r?.exact_alarm === 'granted')
        .catch(() => false) ?? Promise.resolve(false);

    Promise.all([ensureAdhanChannel(plugin), exactAllowed()])
      .then(([, exact]) =>
        plugin.schedule({
          notifications: scheduled.slice(0, MAX_NATIVE).map(({ key, at }, i) => ({
            id: NATIVE_ID_BASE + i,
            title: `حان الآن وقت صلاة ${prayerLabel(key)}`,
            body,
            schedule: { at, allowWhileIdle: true },
            isExactNotification: exact,
            channelId: ADHAN_CHANNEL.id,
            // الآيفون بلا منبّه أذان أصلي: الإشعار نفسه ينادي، والنظام
            // لا يقبل صوت إشعار فوق ثلاثين ثانية — فهو مطلع الأذان
            ...(adhanPlugin() ? {} : { sound: 'adhan.caf' }),
            smallIcon: 'ic_stat_moslam',
            iconColor: '#2E4B8F',
          })),
        })
      )
      .catch((err) => console.warn('تعذرت جدولة التنبيهات:', err));

    return Math.min(scheduled.length, MAX_NATIVE);
  }

  if (notificationPermission() !== 'granted') return 0;

  scheduled.forEach(({ key, at }) => {
    const delay = at.getTime() - Date.now();
    if (delay <= 0 || delay > MAX_TIMEOUT) return;
    timers.push(
      setTimeout(() => notifyWeb(`حان الآن وقت صلاة ${prayerLabel(key)}`, body), delay)
    );
  });

  return timers.length;
}

/** وصف صادق لما تفعله التنبيهات على هذه المنصّة. */
export function notificationCapabilityNote() {
  if (isNativeShell()) return 'ستصلك التنبيهات حتى والتطبيق مغلق.';
  if (!hasWebNotifications()) return 'متصفحك لا يدعم إشعارات النظام.';
  return 'تعمل ما دام التطبيق مفتوحاً في المتصفح (ولو في الخلفية).';
}

/* --------------------------- حالة الإذن وإصلاحه --------------------------- */

/**
 * حالة الإذن الحقيقية دون إظهار نافذة الطلب.
 *
 * notificationPermission() تُرجع 'prompt' على المنصّة الأصلية لأنها
 * متزامنة، وملحق أندرويد لا يجيب إلا بوعد. وهذه تسأل الملحق فعلاً،
 * فنعرف عند الإقلاع أمُنح الإذن أم رُفض دون أن نزعج المستخدم بنافذة.
 */
export async function currentNotificationPermission() {
  if (isNativeShell()) {
    const plugin = nativePlugin();
    if (!plugin) return 'unsupported';
    try {
      const res = await plugin.checkPermissions();
      if (res?.display === 'granted') return 'granted';
      if (res?.display === 'denied') return 'denied';
      return 'prompt';
    } catch {
      return 'prompt';
    }
  }
  return hasWebNotifications() ? Notification.permission : 'unsupported';
}

/**
 * يفتح إعدادات إشعارات التطبيق في النظام.
 *
 * لا مفتاح للتنبيهات في الواجهة: الأذان يعمل دائماً. فإن رفض المستخدم
 * الإذن على مستوى النظام لم يبق له طريق داخل التطبيق إلا هذا الزر —
 * وأندرويد لا يعيد عرض نافذة الطلب بعد رفضين.
 *
 * @returns {Promise<boolean>} هل فُتحت الإعدادات فعلاً؟
 */
export async function openNotificationSettings() {
  const plugin = window.Capacitor?.Plugins?.AppSettings;
  if (!plugin) return false;
  try {
    await plugin.open();
    return true;
  } catch {
    return false;
  }
}


/* ------------------------------ الأذان صوتاً ------------------------------ */

/**
 * يجدول رفع الأذان على مستوى النظام.
 *
 * منفصل عن الإشعار عمداً: الإشعار يُعلم بدخول الوقت، وهذا يرفع النداء.
 * ويعمل والتطبيق مغلق لأن المنبّه في النظام لا في الصفحة.
 */
export function scheduleAdhanCalls(prayers, { adhanSound = '' } = {}) {
  const plugin = adhanPlugin();
  if (!plugin) return 0;

  const times = prayers
    .slice(0, MAX_NATIVE)
    .map(({ key, at }) => ({ at: at.getTime(), prayer: prayerLabel(key) }));

  plugin
    .schedule({ times, sound: adhanSound })
    .catch((err) => console.warn('تعذّرت جدولة الأذان:', err));

  return times.length;
}

/** يوقف أذاناً يُرفع الآن. */
export async function stopAdhan() {
  const plugin = adhanPlugin();
  if (!plugin) return;
  try {
    await plugin.stop();
  } catch {
    // لا شيء يُرفع الآن
  }
}

/**
 * هل يُسمح بالمنبّه الدقيق؟
 *
 * أندرويد ١٢ فأحدث يشترط إذناً مستقلاً، وبدونه يؤجّل النظام المنبّه
 * لتوفير البطارية — فيتأخّر الأذان دقائق عن وقته.
 */
export async function canScheduleExactAdhan() {
  const plugin = adhanPlugin();
  if (!plugin) return true;
  try {
    const res = await plugin.canScheduleExact();
    return res?.value !== false;
  } catch {
    return true;
  }
}

/** يفتح إعداد «المنبّهات والتذكيرات» في النظام. */
export async function openExactAlarmSettings() {
  const plugin = adhanPlugin();
  if (!plugin) return false;
  try {
    await plugin.openExactAlarmSettings();
    return true;
  } catch {
    return false;
  }
}


/**
 * ينزّل صوت الأذان ويحفظه على الجهاز، ويعيد مساره المحلي.
 *
 * التنزيل مرة واحدة: الأذان يجب أن يُرفع بلا إنترنت، فلا يُعتمد على
 * الشبكة في وقته.
 */
export async function cacheAdhanSound(url) {
  const plugin = adhanPlugin();
  if (!plugin || !url) return '';
  try {
    const res = await plugin.cacheSound({ url });
    return res?.path ?? '';
  } catch (err) {
    console.warn('تعذّر تنزيل الأذان:', err);
    return '';
  }
}
